import React, { useState, useEffect } from "react";
import AccountApiClient from "../../services/AccountApiClient";
import { Terminal } from "../../types";

interface AddUpdateTerminalProps {
  isOpen: boolean;
  onClose: () => void;
  onSave: (terminal: Omit<Terminal, "id">) => void;
  terminal?: Terminal | null;
  businessId: string;
  accountId: string;
  onRefresh?: () => void;
}

const AddUpdateTerminal: React.FC<AddUpdateTerminalProps> = ({
  isOpen,
  onClose,
  onSave,
  terminal,
  businessId,
  accountId,
  onRefresh,
}) => {
  const [formData, setFormData] = useState({
    identifier: terminal?.identifier || "",
    tid: terminal?.tid || "",
    serialNumber: terminal?.serialNumber || "",
    isActive: terminal?.isActive !== undefined ? terminal.isActive : true,
  });

  const [errors, setErrors] = useState<{ [key: string]: string }>({});
  const [isLoading, setIsLoading] = useState(false);
  const [success, setSuccess] = useState("");

  // Update form data when terminal prop changes
  useEffect(() => {
    if (terminal) {
      const newFormData = {
        identifier: terminal.identifier || "",
        tid: terminal.tid || "",
        serialNumber: terminal.serialNumber || "",
        isActive: terminal.isActive !== undefined ? terminal.isActive : true,
      };
      setFormData(newFormData);
    } else {
      // Reset form for add mode
      setFormData({
        identifier: "",
        tid: "",
        serialNumber: "",
        isActive: true,
      });
    }
    // Clear any existing errors when terminal changes
    setErrors({});
    setSuccess("");
  }, [terminal]);

  const handleInputChange = (key: string, value: string | boolean) => {
    setFormData((prev) => ({
      ...prev,
      [key]: value,
    }));
    // Clear error when user starts typing
    if (errors[key]) {
      setErrors((prev) => ({
        ...prev,
        [key]: "",
      }));
    }
  };

  const validateForm = () => {
    const newErrors: { [key: string]: string } = {};

    // Identifier is only required for new terminals (add mode)
    if (!terminal && !formData.identifier.trim()) {
      newErrors.identifier = "Terminal identifier is required";
    } else if (!terminal && formData.identifier.trim().length < 4) {
      newErrors.identifier =
        "Terminal identifier must be at least 4 characters";
    }

    if (!formData.tid.trim()) {
      newErrors.tid = "Terminal ID (TID) is required";
    } else if (!terminal && formData.tid.trim().length < 6) {
      newErrors.tid = "Terminal TID must be at least 6 characters";
    }

    if (!formData.serialNumber.trim()) {
      newErrors.serialNumber = "Serial number is required";
    } else if (!terminal && formData.serialNumber.trim().length < 6) {
      newErrors.serialNumber =
        "Terminal serial number must be at least 8 characters";
    }

    setErrors(newErrors);
    const isValid = Object.keys(newErrors).length === 0;
    return isValid;
  };

  const handleSave = async () => {
    const isValid = validateForm();

    if (isValid) {
      setIsLoading(true);
      setSuccess("");
      try {
        let response;
        if (terminal) {
          // Update existing terminal
          response = await AccountApiClient.updateTerminal(
            businessId,
            accountId,
            terminal.id,
            {
              tid: formData.tid,
              serialNumber: formData.serialNumber,
              isActive: formData.isActive,
            }
          );
        } else {
          // Add new terminal (isActive is always true for new terminals)
          response = await AccountApiClient.addTerminal(businessId, accountId, {
            identifier: formData.identifier,
            tid: formData.tid,
            serialNumber: formData.serialNumber,
            isActive: true,
          });
        }

        // Check if response status is 200 (success)
        if (response && (response.status === 200 || response.status === 201)) {
          // Show success toast
          setSuccess(
            terminal
              ? "Terminal updated successfully!"
              : "Terminal added successfully!"
          );

          // Call parent's onSave callback
          onSave(formData);

          // Refresh the account list
          if (onRefresh) {
            onRefresh();
          }

          // Close the modal after showing success message for 3 seconds
          setTimeout(() => {
            onClose();
            // Clear success message (form will be reset by useEffect when modal reopens)
            setSuccess("");
          }, 3000);
        }
      } catch (error) {
        console.error(
          terminal ? "Error updating terminal:" : "Error adding terminal:",
          error
        );
        // You might want to show an error message to the user
      } finally {
        setIsLoading(false);
      }
    }
  };

  const handleCancel = () => {
    onClose();
    // Clear errors and success message (form data will be reset by useEffect when modal reopens)
    setErrors({});
    setSuccess("");
  };

  return (
    <>
      {/* Overlay */}
      {isOpen && <div className="slide-overlay" onClick={onClose}></div>}

      {/* Slide Panel */}
      <div className={`slide-panel ${isOpen ? "slide-panel-open" : ""}`}>
        <div className="slide-panel-header">
          <h2>{terminal ? "Update Terminal" : "Add New Terminal"}</h2>
          <button className="slide-panel-close" onClick={onClose}>
            ✕
          </button>
        </div>

        <div className="slide-panel-content">
          {success && (
            <div className="success-message" style={{ margin: "0 0 1rem 0" }}>
              ✅ {success}
            </div>
          )}

          <div className="form-group">
            <label>Terminal Identifier</label>
            <input
              type="text"
              value={formData.identifier}
              onChange={(e) => handleInputChange("identifier", e.target.value)}
              placeholder="e.g., bar-t1"
              minLength={4}
              className={`form-input ${
                errors.identifier ? "form-input-error" : ""
              }`}
              disabled={!!terminal}
            />
            {errors.identifier && (
              <span className="form-error">{errors.identifier}</span>
            )}
            {terminal && (
              <small style={{ color: "#6b7280", fontSize: "0.875rem" }}>
                Identifier cannot be changed when editing
              </small>
            )}
          </div>

          <div className="form-group">
            <label>Terminal ID (TID) *</label>
            <input
              type="text"
              value={formData.tid}
              onChange={(e) => handleInputChange("tid", e.target.value)}
              placeholder="e.g., GT1525027002"
              min="6"
              className={`form-input ${errors.tid ? "form-input-error" : ""}`}
            />
            {errors.tid && <span className="form-error">{errors.tid}</span>}
          </div>

          <div className="form-group">
            <label>Serial Number *</label>
            <input
              type="text"
              value={formData.serialNumber}
              onChange={(e) =>
                handleInputChange("serialNumber", e.target.value)
              }
              placeholder="e.g., last 8 digits 76543210"
              min="8"
              className={`form-input ${
                errors.serialNumber ? "form-input-error" : ""
              }`}
            />
            {errors.serialNumber && (
              <span className="form-error">{errors.serialNumber}</span>
            )}
          </div>

          {terminal && (
            <div className="form-group">
              <label className="checkbox-label">
                <input
                  type="checkbox"
                  checked={formData.isActive}
                  onChange={(e) =>
                    handleInputChange("isActive", e.target.checked)
                  }
                  className="form-checkbox"
                />
                Terminal is Active
              </label>
            </div>
          )}
        </div>

        <div className="slide-panel-footer">
          <button
            className="btn secondary"
            onClick={handleCancel}
            disabled={isLoading}
          >
            Cancel
          </button>
          <button
            className="btn primary"
            onClick={handleSave}
            disabled={isLoading}
          >
            {isLoading
              ? "Adding Terminal..."
              : terminal
              ? "Update Terminal"
              : "Add Terminal"}
          </button>
        </div>
      </div>
    </>
  );
};

export default AddUpdateTerminal;
